import {
  ClickAwayListener,
  makeStyles,
  ListItem,
  ListItemText,
  Typography,
  useInfiniteScroll,
  useBreakpoint
} from '@paintscout/ui'
import type { MuiTheme as Theme, ListItemProps, StyleClasses } from '@paintscout/ui'
import type { Contact } from '@paintscout/api'
import React, { useRef } from 'react'
import moment from 'moment'
import { formatName } from '@paintscout/util/builder'

const getContactSourceDisplayName = (source: string): string => {
  switch (source) {
    case 'pipeline_deals':
      return 'Pipeline CRM'
    case 'service_titan':
      return 'Service Titan'
    case 'salesforce':
      return 'Salesforce'
    default:
      return ''
  }
}

const useStyles = makeStyles<Theme, ContactResultsListProps>(
  (theme) => ({
    root: {
      height: '100%',
      maxHeight: '60vh',
      overflow: 'auto',
      [theme.breakpoints.down('sm')]: {
        maxHeight: '30vh'
      },
      [theme.breakpoints.down('xs')]: {
        maxHeight: '60vh'
      }
    },
    row: {}
  }),
  { name: 'ContactResultsList' }
)

export interface ContactResultsListProps {
  classes?: StyleClasses<typeof useStyles>
  disabled?: boolean
  results: Array<Partial<Contact> & { dealId?: number }>
  onLoadMore: () => any
  onContactClick: (contact: Partial<Contact & { dealId?: number }>) => any
  onClickAway?: (event: React.MouseEvent<Document, MouseEvent>) => void
}

function ContactResultsList(props: ContactResultsListProps) {
  const classes = useStyles(props)
  const { disabled, results, onContactClick, onLoadMore, onClickAway } = props
  const ref = useRef(null)

  useInfiniteScroll({ ref, onLoadMore, scrollPercentage: 50, disabled })

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        if (onClickAway) onClickAway(event)
      }}
    >
      <div className={classes.root} ref={ref}>
        {results.map((contact, index) => (
          <ContactListItem
            className={`contact-list-${index}`}
            key={index}
            disabled={disabled}
            contact={contact}
            onClick={() => onContactClick(contact)}
          />
        ))}
      </div>
    </ClickAwayListener>
  )
}

/*******************************
 *         ContactListItem     *
 *******************************/
const useListItemStyles = makeStyles((theme: Theme) => ({
  listItem: {
    maxWidth: '100%',
    overflowWrap: 'normal',
    display: 'flex',
    justifyContent: 'space-between'
  },
  listItemText: {
    flex: 1,
    paddingRight: theme.spacing(1)
  },
  secondaryText: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    fontStyle: 'italic',
    overflowWrap: 'normal',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '75%'
    }
  },
  sourceName: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    fontStyle: 'italic',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(),
      maxWidth: '160px'
    }
  },
  email: {
    fontStyle: 'italic',
    overflowWrap: 'break-word',
    wordBreak: 'break-all',
    maxWidth: 280,
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      maxWidth: 220,
      paddingRight: theme.spacing()
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: 180
    }
  },
  owner: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 280,
    paddingRight: theme.spacing(2),
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 220,
      paddingRight: theme.spacing()
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: 180
    }
  }
}))

function ContactListItem({
  contact,
  ...props
}: {
  contact: Partial<Contact>
} & ListItemProps) {
  const classes = useListItemStyles({})
  const isSmallScreen = useBreakpoint((breakpoints) => breakpoints.down('sm'))

  const primaryText = (
    <>
      <strong>
        {contact.first_name} {contact.last_name}
      </strong>
      {!!contact.company && ` - ${contact.company}`}
      {!!contact.stAppointmentDate && ` - ${moment(contact.stAppointmentDate)}`.split(' GMT')[0]}
    </>
  )

  const secondaryText = [contact.address, contact.city, contact.province].filter((item) => item).join(', ')
  const textBodySize = isSmallScreen ? 'body2' : 'body1'
  let contactSource = getContactSourceDisplayName(contact.source)
  let contactSourceSecondary = ''
  if (contact?.pldDeals?.length) {
    if (contact.pldDeals.length > 1) {
      contactSource = `Deal Count: ${contact.pldDeals.length}`
    } else {
      contactSource = `${contact.pldDeals[0].id} `
      contactSourceSecondary = `${contact.pldDeals[0].name}`
    }
  }

  return (
    <ListItem className={classes.listItem} divider button {...(props as any)}>
      <ListItemText
        classes={{ root: classes.listItemText, secondary: classes.secondaryText }}
        primary={primaryText}
        secondary={secondaryText}
        primaryTypographyProps={{
          variant: textBodySize
        }}
        secondaryTypographyProps={{
          variant: textBodySize
        }}
      />

      <div>
        <Typography variant={textBodySize} classes={{ root: classes.sourceName }} align="right">
          {contactSource}
        </Typography>
        {contactSourceSecondary && (
          <Typography variant={textBodySize} classes={{ root: classes.sourceName }} align="right">
            {contactSourceSecondary}
          </Typography>
        )}
        <Typography variant={textBodySize} classes={{ root: classes.email }} align="right">
          {contact.email}
        </Typography>
        {contact.owner?.firstName && (
          <Typography variant={textBodySize} classes={{ root: classes.owner }} align="right">
            {formatName({ firstName: contact.owner?.firstName, lastName: contact.owner?.lastName })}
          </Typography>
        )}
      </div>
    </ListItem>
  )
}

export default ContactResultsList
